import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { ChatMessage } from "../../types/chat.types";
import { saveMessage, askInSession, submitFeedback } from "../../store/slices/chatSlice";
import { MarkdownRenderer } from "../../utils/markdownRenderer";
import { InteractiveQuestion, isInteractiveQuestion } from "./InteractiveQuestion";
import PdfViewer from "../PdfViewer/PdfViewer";
import { LoadingIndicator } from "../LoadingIndicator/LoadingIndicator";
import { RatingButtons } from "./RatingButtons";
import "./MessageList.css";
import "./Markdown.css";

// Constants
const NO_ANSWER_PATTERNS = [
  "Keine Antwort gefunden",
  "Die gesuchte Antwort wurde in der Wissensdatenbank nicht gefunden",
  "no answer found",
  "answer not found",
];

// Types
interface MessageListProps {
  messages: ChatMessage[];
}

interface UserMessageProps {
  question: string;
}

interface AIMessageProps {
  message: ChatMessage;
  sessionId: string;
}

// Utility Components
const SaveButton: React.FC<{
  isSaved: boolean;
  onClick: () => void;
}> = ({ isSaved, onClick }) => (
  <button
    className={`action-btn ${isSaved ? "active" : ""}`}
    onClick={onClick}
    title={isSaved ? "Nachricht gespeichert" : "Nachricht speichern"}
    disabled={isSaved}
  >
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
      <path
        d="M19 21L12 16L5 21V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V21Z"
        stroke="currentColor"
        strokeWidth="2"
        fill={isSaved ? "currentColor" : "none"}
      />
    </svg>
    {isSaved && <span style={{ marginLeft: "4px", fontSize: "12px" }}>✓</span>}
  </button>
);

const ReferencesButton: React.FC<{
  showReferences: boolean;
  referencesCount: number;
  onClick: () => void;
}> = ({ showReferences, referencesCount, onClick }) => (
  <button className="references-btn" onClick={onClick} title="Quellen anzeigen">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d={showReferences ? "M18 15L12 9L6 15" : "M6 9L12 15L18 9"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    <span>Quellen ({referencesCount})</span>
  </button>
);

const ReferenceChip: React.FC<{
  reference: any;
  index: number;
  onClick: (ref: any) => void;
}> = React.memo(({ reference, index, onClick }) => {
  // Format document name (GA013.pdf -> GA013)
  const formatDocName = (name: string) => {
    if (!name) return "Unbekannt";
    return name.replace(/\.pdf$/i, "");
  };

  const docName = formatDocName(reference.document_name);
  const pageNum = reference.page_number;

  // Build display text
  const displayText = pageNum ? `${docName}, S. ${pageNum}` : docName;
  const tooltipText = pageNum
    ? `${docName} - Seite ${pageNum}`
    : docName;

  // Source number: index is 0-based from the reference object, +1 for display
  const sourceNum = (reference.index != null ? reference.index : index) + 1;

  return (
    <div
      className="reference-chip clickable-reference"
      onClick={() => onClick(reference)}
      title={`Klicken um Dokument anzuzeigen: ${tooltipText}`}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        style={{ marginRight: "6px" }}
      >
        <path
          d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14 2V8H20"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="reference-title">[{sourceNum}] {displayText}</span>
    </div>
  );
});

// Main Components
const MessageList: React.FC<MessageListProps> = ({ messages }) => {
  const { loading, currentSessionId } = useAppSelector((state) => state.chat);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  return (
    <div className="message-list">
      {messages.map((message) => (
        <div key={message.id} className="message-pair">
          <UserMessage question={message.question} />
          {currentSessionId && (
            <AIMessage message={message} sessionId={currentSessionId} />
          )}
        </div>
      ))}
      {loading && <LoadingIndicator />}
      <div ref={messagesEndRef} />
    </div>
  );
};

const UserMessage: React.FC<UserMessageProps> = React.memo(({ question }) => (
  <div className="message-container user-message-container">
    <div className="message user-message">{question}</div>
  </div>
));

const AIMessage: React.FC<AIMessageProps> = React.memo(({ message, sessionId }) => {
  const dispatch = useAppDispatch();
  const [showReferences, setShowReferences] = useState(false);
  const [selectedReference, setSelectedReference] = useState<any>(null);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [isDirectLink, setIsDirectLink] = useState(false);

  const allReferences = useMemo(() => message.answer.references || [], [message.answer.references]);

  // Filter references to only those actually cited in the answer text as [Quelle X] or [X]
  const references = useMemo(() => {
    if (!message.answer.content) return [];
    const content = message.answer.content;
    const citedIndices = new Set<number>();
    const pattern = /\[Quelle[^\]]*\]|\[(\d+)\]/g;
    let match;
    while ((match = pattern.exec(content)) !== null) {
      if (match[1]) {
        citedIndices.add(parseInt(match[1], 10));
      } else {
        const nums = match[0].match(/\d+/g);
        if (nums) {
          nums.forEach(n => citedIndices.add(parseInt(n, 10)));
        }
      }
    }
    if (citedIndices.size === 0) return allReferences;
    return allReferences.filter((ref: any) => {
      const refNum = (ref.index != null ? ref.index : 0) + 1;
      return citedIndices.has(refNum);
    });
  }, [message.answer.content, allReferences]);

  const noAnswerFound = useMemo(() =>
    NO_ANSWER_PATTERNS.some((pattern) =>
      message.answer.content.toLowerCase().includes(pattern.toLowerCase())
    ), [message.answer.content]);

  const isInteractive = useMemo(() =>
    isInteractiveQuestion(message.answer.content), [message.answer.content]);

  const handleSave = useCallback(() => {
    dispatch(saveMessage({ sessionId, messageId: message.id }));
  }, [dispatch, sessionId, message.id]);

  const handleRate = useCallback((rating: 'up' | 'down', comment?: string) => {
    dispatch(submitFeedback({
      session_id: sessionId,
      messageId: message.id,
      rating,
      comment
    }));
  }, [dispatch, sessionId, message.id]);

  const handleReferenceClick = useCallback((reference: any, directLink: boolean = false) => {
    const searchResult = {
      document_name: reference.document_name,
      excerpt: reference.excerpt || reference.content || "",
      context: reference.context || "",
      index: reference.index,
      marker: reference.marker || "",
      similarity: reference.similarity,
      page_number: reference.page_number
    };
    setSelectedReference(searchResult);
    setIsDirectLink(directLink);
    setPdfViewerOpen(true);
  }, []);

  const handleOptionClick = useCallback((selectedOption: string) => {
    dispatch(askInSession({
      sessionId,
      question: { question: selectedOption }
    }));
  }, [dispatch, sessionId]);

  if (!message.answer.content) {
    return null;
  }

  return (
    <div className="ai-message-wrapper">
      <div className="message-container ai-message-container">
        <div className="message ai-message">
          <div className="message-content">
            {isInteractive ? (
              <InteractiveQuestion
                content={message.answer.content}
                onOptionClick={handleOptionClick}
              />
            ) : (
              <MarkdownRenderer
                content={message.answer.content}
                references={references}
                onReferenceClick={handleReferenceClick}
              />
            )}
          </div>

          {/* KI-Disclaimer */}
          <div className="ai-disclaimer">
            KI-generierte Antworten können Fehler enthalten. Bitte überprüfen Sie wichtige Informationen in den Originalquellen.
          </div>

          {!noAnswerFound && (
            <div
              className="message-actions"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", margin: "10px 0" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RatingButtons
                  sessionId={sessionId}
                  messageId={message.id}
                  currentRating={message.rating}
                  onRate={handleRate}
                />
                {references.length > 0 && (
                  <ReferencesButton
                    showReferences={showReferences}
                    referencesCount={references.length}
                    onClick={() => setShowReferences(!showReferences)}
                  />
                )}
              </div>
              <SaveButton isSaved={message.saved || false} onClick={handleSave} />
            </div>
          )}
        </div>
      </div>

      {showReferences && references.length > 0 && (
        <div className="references-section">
          <div className="references-title">Quellen:</div>
          <div className="references-list">
            {references.map((ref, index) => (
              <ReferenceChip
                key={`ref-${index}`}
                reference={ref}
                index={index}
                onClick={handleReferenceClick}
              />
            ))}
          </div>
        </div>
      )}

      {selectedReference && (
        <PdfViewer
          open={pdfViewerOpen}
          onClose={() => setPdfViewerOpen(false)}
          reference={selectedReference}
          isDirectLink={isDirectLink}
        />
      )}
    </div>
  );
});

export default MessageList;
